import { useList, useNavigation, useOne, useUpdate } from '@refinedev/core';
import { useParams, useNavigate } from 'react-router';
import { useState, useEffect } from 'react';
import { Edit, Lock, ArrowLeft, Save, Loader2 } from 'lucide-react';
import type { HomeSection } from '@natsepa/database';
import { BilingualField } from '../../components/BilingualField';
import { SingleImageUpload } from '../../components/SingleImageUpload';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';
// Seeded backgrounds are static files served by the public web app; resolve them
// against the web origin so they're visible inside the admin (different domain).
const WEB_URL = import.meta.env.VITE_SITE_URL || 'https://thenatsepa.com';
const resolveBg = (v?: string) => (v && v.startsWith('/') ? `${WEB_URL}${v}` : v);

export function HomeSectionsList() {
  const { data, isLoading, error } = useList<HomeSection>({ resource: 'home-sections' });
  const { edit } = useNavigation();

  if (isLoading) return <div className="p-4">Loading...</div>;
  if (error) return (
    <div className="p-4"><div className="bg-red-50 border border-red-200 rounded-lg p-4"><p className="text-red-800">Error loading home sections: {error.message}</p></div></div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Home Sections</h1>
          <p className="text-sm text-gray-500 mt-1">Homepage section content</p>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-2">
          <Lock className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-800">Section keys and background images are locked</p>
            <p className="text-sm text-blue-600">You can edit titles, descriptions, and content only.</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Section Key</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {data?.data.map((section) => (
              <tr key={section.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <Lock className="w-4 h-4 text-gray-400" />
                    <code className="px-2 py-1 bg-gray-100 rounded text-sm font-mono">{section.section}</code>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap"><div className="font-medium text-gray-900">{section.title || '-'}</div></td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <button onClick={() => edit('home-sections', section.id)} className="p-2 text-gray-400 hover:text-gold-600 transition-colors" title="Edit Content">
                    <Edit className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data?.data.length === 0 && <div className="p-8 text-center text-gray-500">No home sections found.</div>}
      </div>
    </div>
  );
}

// Edit Home Section
export function HomeSectionsEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, isLoading } = useOne<HomeSection>({ resource: 'home-sections', id: id! });
  const { mutate: updateSection, isPending } = useUpdate();

  const [form, setForm] = useState({
    titleEn: '',
    titleId: '',
    descriptionEn: '',
    descriptionId: '',
    subtitle: '',
    buttonTextEn: '',
    buttonTextId: '',
    buttonLink: '',
  });

  useEffect(() => {
    if (data?.data) {
      const section = data.data;
      const content = section.content as { subtitle?: string; buttonTextEn?: string; buttonTextId?: string; buttonLink?: string } | undefined;
      setForm({
        titleEn: (section as any).titleEn || (section as any).title_en || section.title || '',
        titleId: (section as any).titleId || (section as any).title_id || section.title || '',
        descriptionEn: (section as any).descriptionEn || (section as any).description_en || section.description || '',
        descriptionId: (section as any).descriptionId || (section as any).description_id || section.description || '',
        subtitle: content?.subtitle || '',
        buttonTextEn: content?.buttonTextEn || '',
        buttonTextId: content?.buttonTextId || '',
        buttonLink: content?.buttonLink || '',
      });
    }
  }, [data]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const content: Record<string, string> = {};
    if (form.subtitle) content.subtitle = form.subtitle;
    if (form.buttonTextEn) content.buttonTextEn = form.buttonTextEn;
    if (form.buttonTextId) content.buttonTextId = form.buttonTextId;
    if (form.buttonLink) content.buttonLink = form.buttonLink;
    updateSection(
      {
        resource: 'home-sections',
        id: id!,
        values: {
          titleEn: form.titleEn,
          titleId: form.titleId,
          descriptionEn: form.descriptionEn,
          descriptionId: form.descriptionId,
          content: Object.keys(content).length > 0 ? content : undefined,
        },
      },
      { onSuccess: () => navigate('/home-sections') }
    );
  };

  if (isLoading) return <div className="p-4">Loading...</div>;

  const section = data?.data;

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate('/home-sections')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors"><ArrowLeft className="w-5 h-5" /></button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Edit Home Section</h1>
          <p className="text-sm text-gray-500 mt-1">{section?.section || 'Section details'}</p>
        </div>
      </div>

      <div className="max-w-3xl">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <Lock className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-800">Section key is locked</p>
                <p className="text-sm text-blue-600">The section key is managed by developers. Title, description, and background image can be edited.</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Section Info</h2>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Section Key</label>
              <div className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-gray-400" />
                <code className="px-3 py-2 bg-gray-100 rounded-lg text-sm font-mono">{section?.section}</code>
              </div>
            </div>
            <SingleImageUpload
              label="Background Image"
              currentImage={resolveBg(section?.backgroundImage)}
              uploadUrl={`${API_URL}/admin/home-sections/${id}/image`}
              deleteUrl={`${API_URL}/admin/home-sections/${id}/image`}
            />
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Content</h2>

            <BilingualField
              label="Title"
              valueEn={form.titleEn}
              valueId={form.titleId}
              onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'titleEn' : 'titleId']: value })}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
              <input
                type="text"
                value={form.subtitle}
                onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
                placeholder="Optional subtitle text"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">Short text displayed below the title (optional)</p>
            </div>

            <BilingualField
              label="Description"
              valueEn={form.descriptionEn}
              valueId={form.descriptionId}
              onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'descriptionEn' : 'descriptionId']: value })}
              multiline
              rows={4}
            />
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Button (Optional)</h2>
            <p className="text-sm text-gray-500">For CTA sections with a button link (e.g., Book Now).</p>

            <BilingualField
              label="Button Text"
              valueEn={form.buttonTextEn}
              valueId={form.buttonTextId}
              onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'buttonTextEn' : 'buttonTextId']: value })}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Button Link</label>
              <input
                type="text"
                value={form.buttonLink}
                onChange={(e) => setForm({ ...form, buttonLink: e.target.value })}
                placeholder="/rooms or https://..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => navigate('/home-sections')} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">Cancel</button>
            <button type="submit" disabled={isPending} className="flex items-center gap-2 px-4 py-2 bg-gold-600 text-white rounded-lg hover:bg-gold-700 transition-colors disabled:opacity-50">
              {isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
